import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';
import { clientStore } from 'apps/client/app';

import Heading from 'components/Heading';
import Button from 'components/Button';
import Dropdown from 'components/Dropdown';
import Tooltip from 'components/Tooltip';

import styled from 'styled-components';
import { media, customMedia } from 'styles/utils';

// FIXME This can be a functional component
@withNamespaces()
@observer
export class Section extends React.Component {
  render() {
    const {
      className,
      first,
      last = false,
      title,
      titleIcon = 'add',
      subTitle,
      subTitleColor = '',
      tooltip,
      children,
      invisible,
      sectionConfig,
      createHandler,
      noSort,
      noCount,
      noMarginTop,
      actions,
      gridColumns,
      gridGap,
      intercomTarget = '',
    } = this.props;

    // FIXME Remove let logic from the render
    let sortOptions;
    let phases;
    let selectedPhase;
    let sectionSortIndex;
    if (sectionConfig) {
      sortOptions = sectionConfig.parent && sectionConfig.parent.sortOptions;
      phases = sectionConfig.manager && sectionConfig.manager.phases;
      const { manager } = sectionConfig;
      const phaseId = (manager.filterBy ?? []).filter(
        filterByItem => filterByItem[0] === 'phaseId',
      )[0]?.[1];
      selectedPhase =
        phases &&
        manager &&
        phaseId &&
        phases.findIndex(phase => phase.id === phaseId);

      sectionSortIndex =
        manager?.sortOptions?.findIndex(
          ([optionValue]) => optionValue === manager.sortBy,
        ) || 0;
    }
    return (
      <Main
        className={className}
        first={first}
        last={last}
        noTitle={title === ''}
        ref={section => {
          this.section = section;
        }}
        noMarginTop={noMarginTop}
        data-intercom-target={intercomTarget}
      >
        <SectionHeader noSpacing={title === ''}>
          <>
            <Tooltip id='tooltipSection' delayShow={500} place='right' />
            {title && (
              <HeadingStyled
                invisible={invisible}
                id={
                  typeof title === 'string' &&
                  title.replace(/\s+/g, '-').toLowerCase()
                }
                level={2}
                size='large'
              >
                {title}
                {!noCount &&
                sectionConfig &&
                !subTitle &&
                !sectionConfig.parent.loadingMore ? (
                  <Count>{`(${sectionConfig.parent.totalCount})`}</Count>
                ) : (
                  ''
                )}
                {subTitle && (
                  <Count subTitleColor={subTitleColor}>{subTitle}</Count>
                )}
              </HeadingStyled>
            )}
            {createHandler &&
              !['x', 'xs'].includes(clientStore.activeBreakPoint) && (
                <ButtonStyled
                  icon={titleIcon}
                  ariaLabel='Add'
                  onClick={createHandler}
                  size='xsmall'
                  data-tip={tooltip}
                  data-for='tooltipSection'
                  outline
                />
              )}
          </>
          <SpaceHoarder />
          <DropdownWrapper>
            {phases && (
              <Dropdown
                items={phases.map(phase => ({
                  text: phase.name,
                  value: phase.id,
                }))}
                trigger={
                  <>
                    <Trans>Phase</Trans>:
                  </>
                }
                placeholder='All'
                selectedItemIndex={selectedPhase || 0}
                onItemSelected={phase => {
                  const filter = sectionConfig.manager.filterBy.filter(
                    filterByItem => filterByItem[0] !== 'phaseId',
                  );
                  if (phase.value) {
                    filter.push(['phaseId', phase.value]);
                  }
                  sectionConfig.manager.SetFilter(filter);
                }}
              />
            )}
            {sortOptions && sortOptions.length > 0 && !noSort && (
              <Dropdown
                items={sortOptions.map(opt => ({
                  text: opt[1],
                  key: opt[0],
                  value: opt[0],
                }))}
                trigger={
                  <>
                    <Trans>Sort by</Trans>:
                  </>
                }
                icon='sort'
                placeholder={sortOptions[0][1]}
                selectedItemIndex={sectionSortIndex}
                onItemSelected={sortOption =>
                  sectionConfig.manager.SetSort(sortOption.value)
                }
              />
            )}
          </DropdownWrapper>
          {actions}
        </SectionHeader>
        <Grid gridColumns={gridColumns} gridGap={gridGap}>
          {children}
        </Grid>
      </Main>
    );
  }
}

// FIXME Styles should be in another file
const Main = styled.section`
  width: 100%;
  height: auto;
  display: inline-block;
  position: relative;
  margin: ${props =>
    props.noMarginTop
      ? `0 auto ${props.theme.spacing.default}`
      : props.last
      ? `${props.theme.spacing.default} auto ${props.theme.spacing.xlarge}`
      : `${props.theme.spacing.large} auto ${props.theme.spacing.xlarge} auto`};
  & + & {
    margin: ${props => props.theme.spacing.large} auto;
  }
  & > & {
    margin: ${props => props.theme.spacing.large} auto 0;
  }
`;

export const SectionHeader = styled.header`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => !props.noSpacing && props.theme.spacing.medium};
  ${customMedia.lessThan('medium')`
    flex-direction: column;
    align-items: flex-start;
  `};
`;

const DropdownWrapper = styled.div(({ theme }) => ({
  [`@media(max-width: ${theme.breakpoints.medium})`]: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

const SpaceHoarder = styled.div`
  flex: 999;
`;

const HeadingStyled = styled(Heading)`
  transition: opacity 300ms ease-in-out;
  opacity: ${props => (props.invisible ? '0' : '1')};
  pointer-events: none;
  ${customMedia.lessThan('medium')`
    margin-bottom: ${({ theme }) => theme.space[4]};
    padding-left: ${({ theme }) => theme.space[4]};
  `};
`;

export const Count = styled.span`
  color: ${props =>
    props.subTitleColor ? props.subTitleColor : props.theme.color.wfGrey400};
  margin-left: ${props => props.theme.spacing.small};
  font-weight: ${props => props.theme.font.weight.regular};
`;

const ButtonStyled = styled(Button)`
  display: inline-block;
  transition: opacity 200ms ease-in-out;
  vertical-align: baseline;
  margin-left: ${props => props.theme.spacing.default};
  margin-right: auto;
`;

const Grid = styled.div`
  display: ${props => props.gridColumns && 'grid'};
  grid-template-columns: repeat(1, 1fr);
  grid-gap: ${props =>
    props.theme.spacing[props.gridGap] || props.theme.spacing.large};

  ${media.medium`
    grid-template-columns: repeat(2, 1fr);
  `}
  ${media.large`
    grid-template-columns: repeat(${props => props.gridColumns}, 1fr);
  `}
`;

Section.propTypes = {
  className: PropTypes.string,
  first: PropTypes.bool,
  last: PropTypes.bool,
  title: PropTypes.string,
  titleIcon: PropTypes.string,
  subTitle: PropTypes.string,
  subTitleColor: PropTypes.string,
  tooltip: PropTypes.string,
  index: PropTypes.number,
  gridColumns: PropTypes.number,
  gridGap: PropTypes.string,
  updateHeader: PropTypes.bool,
  children: PropTypes.node,
  invisible: PropTypes.bool,
  noSort: PropTypes.bool,
  noCount: PropTypes.bool,
  noMarginTop: PropTypes.bool,
  actions: PropTypes.element,
  createHandler: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  sectionConfig: PropTypes.object,
  intercomTarget: PropTypes.string,
};
